// i18n-ns: budget
// (the keys below are resolved with the caller's `t`, so there is no useTranslation call for
// scripts/i18nKeys.js to read the namespace from)
import { useMemo, useRef } from 'react';
import type { PendingEdit } from '../../hooks/usePendingCellEdits';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The blocking problem plus the row it is in — the caller names that row, because the pending set spans
 *  rows the page's filters may not be rendering. */
export interface ConfigEditIssue {
    message: string;
    rowId: number;
}

/** A cell the user emptied. */
const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

interface LabelledConfig {
    id: number;
    partnerName?: string | null;
    locationName?: string | null;
    workingUnitName?: string | null;
    costCenter?: string | null;
}

/** Config id → the label the grids show it under, for naming the row an issue is in. Typed structurally
 *  rather than against BillingConfigWithRelations: it needs four display strings and nothing else. */
export const configRowLabels = (configs: LabelledConfig[]): Map<number, string> =>
    new Map(
        configs.map((config) => [
            config.id,
            [config.partnerName, config.locationName, config.workingUnitName, config.costCenter]
                .filter(Boolean)
                .join(' – ') || `#${config.id}`,
        ])
    );

/**
 * The same map, but ACCUMULATED across renders (CALL-779).
 *
 * The pending store spans every filter — an edit made in one bucket is still pending, and still saved,
 * under another — but the config query is server-filtered by bucket, so the rows a blocking edit belongs
 * to may not be in the current response at all. A map built from that response alone therefore lost
 * exactly the label the save bar needs, leaving a generic message for a row the user cannot see.
 * Labels never go stale in a way that matters (a renamed row relabels on its next fetch), so keeping the
 * ones already seen costs nothing.
 */
export const useConfigRowLabels = (configs: LabelledConfig[]): Map<number, string> => {
    const retained = useRef(new Map<number, string>());

    return useMemo(() => {
        for (const [id, label] of configRowLabels(configs)) retained.current.set(id, label);
        return new Map(retained.current);
    }, [configs]);
};

// What stops a page of pending edits from being saved (CALL-779), as one message for the save bar.
//
// The rules are the create/edit dialog's — the grid columns edit the same three fields with the same
// constraints, and before this the grid enforced none of them: an unparseable Service Faktor was
// rewritten to 1 on its way to the API (`Number.isNaN(parsed) ? 1 : parsed`), so a typo silently became
// a real value instead of being refused.
//
// The monthly kinds have no rules of their own: their columns declare a valueParser, so a cell is
// already `number | null` by the time it reaches the pending store, and `null` legitimately means
// "delete this month". The default branch below is therefore a guard rather than validation — a column
// added later WITHOUT a parser would put a raw string ("1.000", which the backend's z.coerce.number()
// would happily read as 1) in front of the user as a blocked save instead of storing it silently.
export const firstConfigEditIssue = (edits: PendingEdit[], t: Translate): ConfigEditIssue | null => {
    for (const edit of edits) {
        const issue = (key: string): ConfigEditIssue => ({ message: t(key), rowId: edit.rowId });

        if (edit.kind === 'field') {
            if (edit.key === 'costCenter' && isBlank(edit.value)) {
                return issue('validation.costCenterRequired');
            }

            if (edit.key === 'serviceFactor') {
                // A cleared Service Faktor is not "unset": the column is NOT NULL with a default of 1,
                // and the backend coerces null to 0 — a 0 factor zeroes the config's handling cost.
                if (isBlank(edit.value) || !Number.isFinite(Number(edit.value))) {
                    return issue('validation.invalidNumber');
                }
                if (Number(edit.value) < 0) {
                    return issue('validation.serviceFactorNegative');
                }
            }

            if (edit.key === 'defaultAbt' && !isBlank(edit.value)) {
                const parsed = Number(edit.value);
                if (!Number.isInteger(parsed) || parsed <= 0) {
                    return issue('validation.defaultAbtPositiveInteger');
                }
            }

            // CALL-824: a cleared cell is a legitimate "no fallback"; a value must be a non-negative number.
            if (edit.key === 'defaultMinutePrice' && !isBlank(edit.value)) {
                const parsed = Number(edit.value);
                if (!Number.isFinite(parsed) || parsed < 0) {
                    return issue('validation.defaultMinutePriceNonNegative');
                }
            }

            continue;
        }

        if (!isBlank(edit.value) && !Number.isFinite(Number(edit.value))) {
            return issue('validation.invalidNumber');
        }
    }

    return null;
};
